import { api } from "@/api/client";

export type Marketplace = "wb" | "ozon" | "letu" | "ym";

/** Человекочитаемое имя маркетплейса — В ОДНОМ месте.
 *
 * ⚠️ До этого подписи стояли тернарником `mp === "wb" ? "Wildberries" : "Ozon"`
 * в трёх экранах: любой третий маркетплейс молча подписался бы как «Ozon», и
 * заметить это можно было бы только глазами кладовщика. */
export const MARKETPLACE_TITLES: Record<Marketplace, string> = {
  wb: "Wildberries",
  ozon: "Ozon",
  letu: "Лэтуаль",
  ym: "Яндекс.Маркет",
};

/** Подпись маркетплейса. Неизвестный код показываем КАК ЕСТЬ, а не подменяем
 *  знакомым именем: увидев сырой код, человек поймёт, что портал отстал от
 *  бэкенда, а увидев «Ozon» вместо Лэтуаль — не поймёт ничего. */
export function marketplaceTitle(mp: string): string {
  return MARKETPLACE_TITLES[mp as Marketplace] ?? mp;
}

/** Порядок кнопок на экране новой приёмки. */
export const MARKETPLACE_ORDER: Marketplace[] = ["wb", "ozon", "letu", "ym"];

/** Вид возврата маркетплейса — зеркало `RETURN_KINDS` бэкенда.
 *
 * `commission` — товар передан комиссионеру, возврат оформляется приходной
 * накладной. `transfer` — товар уехал перемещением на наш же склад-зеркало и
 * всё время оставался нашим: контрагента и договора у него нет по построению,
 * вместо них нужен склад. Экран «Договоры комиссии» по этому виду и решает,
 * какие поля показывать. */
export type ReturnKind = "commission" | "transfer";
export const RETURN_KINDS: Record<Marketplace, ReturnKind> = {
  wb: "commission",
  ozon: "commission",
  letu: "commission",
  ym: "transfer",
};
export type ScanKind = "datamatrix" | "ean13";
export type ReasonType = "utility" | "repack" | "fbs";
export type ReasonSubtype = "expired" | "illiquid" | "incomplete";
export type SessionStatus = "open" | "completed" | "cancelled";
// Источник партии:
// - `hs`     — партия пришла из ЧЗ и нашлась в 1С (DM happy path);
// - `manual` — оператор ввёл партию руками (или исправил пришедшую из ЧЗ);
// - `none`   — партия не найдена в 1С, оператор подтвердил «Учесть без партии»
//              (partia_ref сохранён как ZERO_GUID).
export type PartiaSource = "hs" | "manual" | "none";

export interface SessionItemView {
  id: string;
  scan_kind: ScanKind;
  raw_scan: string;
  gtin: string | null;
  serial: string | null;
  ean13: string | null;
  nomenclature_ref: string | null;
  nomenclature_name: string | null;
  nomenclature_article: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  partia_ref: string | null;
  partia_name: string | null;
  partia_source: PartiaSource | null;
  reason_type: ReasonType;
  reason_subtype: ReasonSubtype | null;
  one_c_return_doc_ref: string | null;
  one_c_return_doc_number: string | null;
  one_c_return_doc_date: string | null;
  created_at: string;
}

export interface SessionView {
  id: string;
  display_number: number | null;
  marketplace: Marketplace;
  status: SessionStatus;
  user_id: number;
  username: string;
  connection_id: string | null;
  created_at: string;
  completed_at: string | null;
  items_count: number;
}

export interface SessionDetail extends SessionView {
  items: SessionItemView[];
}

export interface LookupRequest {
  scan_kind: ScanKind;
  raw_scan: string;
  gtin?: string;
  serial?: string;
  ean13?: string;
}

export interface LookupResponse {
  scan_kind: ScanKind;
  gtin: string | null;
  serial: string | null;
  ean13: string | null;
  honest_sign_batch_name: string | null;
  honest_sign_product_name: string | null;
  honest_sign_warning: string | null;
  nomenclature_ref: string | null;
  nomenclature_name: string | null;
  nomenclature_article: string | null;
  characteristic_ref: string | null;
  characteristic_name: string | null;
  partia_ref: string | null;
  partia_name: string | null;
  partia_expires_at: string | null;
  partia_production_date: string | null;
  not_found_reason: string | null;
}

export interface ItemCreate {
  scan_kind: ScanKind;
  raw_scan: string;
  gtin?: string | null;
  serial?: string | null;
  ean13?: string | null;
  nomenclature_ref?: string | null;
  nomenclature_name?: string | null;
  nomenclature_article?: string | null;
  characteristic_ref?: string | null;
  characteristic_name?: string | null;
  partia_ref?: string | null;
  partia_name?: string | null;
  partia_source?: PartiaSource | null;
  // Если true — backend ставит partia_ref=ZERO_GUID без валидации
  // (модалка «Учесть без партии»). Требует непустой partia_name.
  accept_without_partia?: boolean;
  reason_type: ReasonType;
  reason_subtype?: ReasonSubtype | null;
}

export async function listSessions(): Promise<SessionView[]> {
  const { data } = await api.get<SessionView[]>("/returns/sessions");
  return data;
}

export async function createSession(marketplace: Marketplace): Promise<SessionView> {
  const { data } = await api.post<SessionView>("/returns/sessions", { marketplace });
  return data;
}

export async function getSession(id: string): Promise<SessionDetail> {
  const { data } = await api.get<SessionDetail>(`/returns/sessions/${id}`);
  return data;
}

export async function completeSession(id: string): Promise<SessionView> {
  const { data } = await api.post<SessionView>(`/returns/sessions/${id}/complete`);
  return data;
}

export async function cancelSession(id: string): Promise<SessionView> {
  const { data } = await api.post<SessionView>(`/returns/sessions/${id}/cancel`);
  return data;
}

export async function reopenSession(id: string): Promise<SessionView> {
  const { data } = await api.post<SessionView>(`/returns/sessions/${id}/reopen`);
  return data;
}

export async function lookupScan(
  sessionId: string,
  body: LookupRequest,
): Promise<LookupResponse> {
  const { data } = await api.post<LookupResponse>(
    `/returns/sessions/${sessionId}/items/lookup`,
    body,
  );
  return data;
}

export async function addItem(
  sessionId: string,
  body: ItemCreate,
): Promise<SessionItemView> {
  const { data } = await api.post<SessionItemView>(
    `/returns/sessions/${sessionId}/items`,
    body,
  );
  return data;
}

export async function deleteItem(sessionId: string, itemId: string): Promise<void> {
  await api.delete(`/returns/sessions/${sessionId}/items/${itemId}`);
}

/**
 * Download the «служебная записка на утилизацию» as a .docx file.
 * Triggers a browser save dialog with the filename from Content-Disposition.
 */
export async function downloadMemo(sessionId: string): Promise<void> {
  const resp = await api.get<Blob>(`/returns/sessions/${sessionId}/memo.docx`, {
    responseType: "blob",
  });
  // Try to recover the filename the backend suggested; fall back to a generic
  // name so the file isn't left as a UUID blob.
  const cd: string = (resp.headers as Record<string, string>)["content-disposition"] ?? "";
  let filename = `СЗ_возвраты_${sessionId.slice(0, 8)}.docx`;
  const m = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
  if (m && m[1]) {
    try {
      filename = decodeURIComponent(m[1]);
    } catch {
      filename = m[1];
    }
  }
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ───── обработка возвратов (desktop) ──────────────────────────────────────

export interface PlannedLine {
  nom_ref: string;
  nom_name: string;
  char_ref: string | null;
  char_name: string | null;
  partia_ref: string;
  partia_name: string;
  qty: number;
  unit_transfer_price: number;
  sum_total: number;
  /** Причина строки — от неё зависит склад-приёмник. */
  reason_type: ReasonType | "";
}

export interface PlannedDocument {
  organization_ref: string;
  organization_name: string;
  kontragent_ref: string;
  kontragent_name: string;
  dogovor_ref: string;
  dogovor_name: string;
  /** Заказы покупателя документа: их много — заказ стоит в СТРОКЕ
   *  (`ПоложениеЗаказаПоставщику = ВТабличнойЧасти`), а не в шапке. */
  order_refs: string[];
  /** Те же заказы подписями «№ABC от dd.mm.yyyy» (не резолвился — UUID). */
  order_labels: string[];
  sum_total: number;
  lines: PlannedLine[];
}

export interface ShortageItem {
  nom_ref: string;
  nom_name: string;
  char_ref: string | null;
  char_name: string | null;
  partia_ref: string;
  partia_name: string;
  needed: number;
  available: number;
  /** С какой причиной позицию приняли. Решение по нехватке у утиля и
   *  переупаковки разное, поэтому причина видна прямо в блоке. */
  reason_type: string;
  reason_subtype: string | null;
}

// Позиция, учтённая БЕЗ партии по фолбэку: партия есть в 1С, но остатка на
// комиссии по её заказам нет. Не блокирует создание — инфо для оператора.
export interface PartiaFallbackItem {
  nom_ref: string;
  nom_name: string;
  char_ref: string | null;
  char_name: string | null;
  partia_ref: string;
  partia_name: string;
  qty: number;
}

export interface PreviewResponse {
  planned_documents: PlannedDocument[];
  shortages: ShortageItem[];
  partia_fallbacks?: PartiaFallbackItem[];
  can_create: boolean;
}

export interface CreatedDoc {
  organization_ref: string;
  organization_name: string;
  ref_key: string | null;
  number: string | null;
  date: string | null;
  posted: boolean;
  sum_total: number;
  /** Заполнены только у документов, созданных ДО объединения (один документ
   *  на заказ). У новых заказов много — их число в `orders_count`. */
  zakaz_ref?: string;
  zakaz_label?: string | null;
  orders_count?: number | null;
  error: string | null;
}

export interface CreateResponse {
  created: CreatedDoc[];
}

export interface ProcessingRunView {
  id: string;
  session_id: string;
  status: string;
  created_at: string;
  result_json: { created?: CreatedDoc[] };
}

export async function getProcessingPreview(sessionId: string): Promise<PreviewResponse> {
  const { data } = await api.get<PreviewResponse>(
    `/returns/sessions/${sessionId}/processing/preview`,
  );
  return data;
}

export async function createProcessing(sessionId: string): Promise<CreateResponse> {
  const { data } = await api.post<CreateResponse>(
    `/returns/sessions/${sessionId}/processing/create`,
  );
  return data;
}

export async function getProcessingState(
  sessionId: string,
): Promise<ProcessingRunView | null> {
  const { data } = await api.get<ProcessingRunView | null>(
    `/returns/sessions/${sessionId}/processing`,
  );
  return data;
}

export async function downloadMemoForOrg(
  sessionId: string,
  organizationRef: string,
  organizationName: string,
): Promise<void> {
  const resp = await api.get<Blob>(
    `/returns/sessions/${sessionId}/processing/memo/${organizationRef}.docx`,
    { responseType: "blob" },
  );
  const cd: string = (resp.headers as Record<string, string>)["content-disposition"] ?? "";
  let filename = `СЗ_${organizationName}_${sessionId.slice(0, 8)}.docx`;
  const m =
    cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
  if (m && m[1]) {
    try {
      filename = decodeURIComponent(m[1]);
    } catch {
      filename = m[1];
    }
  }
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
